import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, X, Sparkles } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useAchievements } from '@/hooks/useAchievements';

export function AchievementUnlock() {
  const { recentUnlock } = useAchievements();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (recentUnlock) {
      setIsVisible(true);
      const timer = setTimeout(() => setIsVisible(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [recentUnlock]);

  const getIcon = (iconName: string) => {
    const icons = LucideIcons as unknown as Record<string, React.ComponentType<{ size?: number; className?: string }>>;
    return icons[iconName] || Trophy;
  };

  if (!recentUnlock) return null;

  const Icon = getIcon(recentUnlock.icon);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: -100, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -50, scale: 0.8 }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] pointer-events-auto"
        >
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-accent/90 to-primary/90 backdrop-blur-xl shadow-2xl shadow-accent/30 border border-white/20">
            {/* Sparkle effects */}
            <motion.div
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute inset-0 bg-white/10"
            />
            
            {/* Floating particles */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ 
                  x: Math.random() * 200, 
                  y: 60,
                  opacity: 0,
                }}
                animate={{ 
                  y: -20,
                  opacity: [0, 1, 0],
                }}
                transition={{ 
                  duration: 2,
                  delay: i * 0.2,
                  repeat: Infinity,
                }}
                className="absolute"
              >
                <Sparkles size={12} className="text-white/50" />
              </motion.div>
            ))}

            <div className="relative flex items-center gap-4 p-4 min-w-[300px]">
              {/* Icon */}
              <motion.div
                initial={{ rotate: -180, scale: 0 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: 'spring', bounce: 0.5, delay: 0.2 }}
                className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center"
              >
                <Icon size={28} className="text-white" />
              </motion.div>

              {/* Content */}
              <div className="flex-1 text-white">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center gap-2"
                >
                  <span className="text-[10px] uppercase tracking-widest font-bold text-white/80">
                    Achievement Unlocked!
                  </span>
                  <Trophy size={12} className="text-yellow-300" />
                </motion.div>
                <motion.p
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-lg font-bold"
                >
                  {recentUnlock.name}
                </motion.p>
                <motion.p
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="text-xs text-white/80"
                >
                  +{recentUnlock.points} Aura Points
                </motion.p>
              </div>

              {/* Close button */}
              <button
                onClick={() => setIsVisible(false)}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/20 transition-colors"
              >
                <X size={14} className="text-white/60" />
              </button>
            </div>

            {/* Progress bar */}
            <motion.div
              initial={{ scaleX: 1 }}
              animate={{ scaleX: 0 }}
              transition={{ duration: 5, ease: 'linear' }}
              className="h-1 bg-white/30 origin-left"
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
